import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryState } from 'nuqs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PATHS } from "@/lib/paths";
import { apiPublic } from '@/lib/api';

type Preview = { email: string; orgName: string; role: string; hasAccount: boolean };

export function AcceptInvitePage() {
  const navigate = useNavigate();
  const [token] = useQueryState('token', { defaultValue: '' });

  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState('');
  // A transient failure (server unreachable, 5xx, non-JSON body) leaves a
  // perfectly valid invite recoverable — we offer "Try Again" rather than
  // dead-ending at "Invite Unavailable". Only a definitive 4xx (invalid /
  // expired / already-used) is treated as terminal.
  const [canRetry, setCanRetry] = useState(false);
  const [loading, setLoading] = useState(true);

  const [displayName, setDisplayName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setPreviewError('');
    setCanRetry(false);
    if (!token) {
      setPreviewError('This invite link is missing its token. Please use the link from your invite email.');
      setLoading(false);
      return;
    }
    try {
      const res = await apiPublic.raw<Preview & { error?: string }>(PATHS.authInvitePreview, {
        method: 'POST',
        body: { token },
        ignoreResponseError: true,
      });
      const data = res._data;

      if (res.ok && data) {
        setPreview(data);
      } else if (res.status >= 400 && res.status < 500 && data?.error) {
        // Definitive: the invite itself is bad (invalid / expired / used).
        setPreviewError(data.error);
      } else {
        // 5xx or an unparseable body — the server hiccupped, invite is fine.
        setPreviewError("We couldn't reach the server. This is usually temporary — please try again.");
        setCanRetry(true);
      }
    } catch {
      // Request rejected outright — connection / DNS / TLS / offline.
      setPreviewError('Network error — check your connection and try again.');
      setCanRetry(true);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiPublic.raw<{ token: string; organizations?: { id: string }[]; error?: string }>(
        PATHS.authAcceptInvite,
        {
          method: 'POST',
          body: { token, password: newPassword, displayName: displayName.trim() || undefined },
          ignoreResponseError: true,
        },
      );
      const data = res._data;
      if (!res.ok || !data) {
        setError(data?.error || 'Failed to accept invite.');
        setSubmitting(false);
        return;
      }
      // Sign the user in straight away.
      localStorage.setItem('auth_token', data.token);
      if (data.organizations?.[0]?.id) {
        localStorage.setItem('active_org_id', data.organizations[0].id);
      }
      navigate('/');
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-[400px]">
        <div className="rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
          <div className="px-8 pt-8 pb-4 text-center">
            <h2 className="text-xl font-semibold text-foreground tracking-tight">
              {loading ? 'Loading…' : preview ? `Join ${preview.orgName}` : canRetry ? "Couldn't Load Invite" : 'Invite Unavailable'}
            </h2>
            {preview && (
              <p className="text-sm text-muted-foreground mt-1">
                Set a password to accept the invite for <span className="text-foreground">{preview.email}</span>.
              </p>
            )}
            {previewError && !preview && (
              <p className="text-sm text-muted-foreground mt-1">{previewError}</p>
            )}
          </div>

          {loading ? (
            <div className="px-8 pb-8 text-center text-xs text-muted-foreground">Checking invite…</div>
          ) : !preview ? (
            <div className="px-8 pb-8 flex flex-col gap-4">
              {canRetry && (
                <Button
                  onClick={loadPreview}
                  className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
                >
                  Try Again
                </Button>
              )}
              <Button
                onClick={() => navigate('/login')}
                variant={canRetry ? 'outline' : 'default'}
                className={canRetry
                  ? 'w-full h-10 font-medium rounded-lg transition-colors'
                  : 'w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors'}
              >
                Go to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-8 pb-8 flex flex-col gap-4">
              {!preview.hasAccount && (
                <div className="space-y-1.5">
                  <Label htmlFor="display-name" className="text-xs text-muted-foreground font-medium">Display Name (optional)</Label>
                  <Input
                    id="display-name"
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    className="bg-white/[0.04] border-border text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 h-10"
                    placeholder="Jane Doe"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="new-pass" className="text-xs text-muted-foreground font-medium">Password</Label>
                <Input
                  id="new-pass"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  className="bg-white/[0.04] border-border text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 h-10"
                  placeholder="••••••••"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm-pass" className="text-xs text-muted-foreground font-medium">Confirm Password</Label>
                <Input
                  id="confirm-pass"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-white/[0.04] border-border text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 h-10"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div className="text-xs px-3 py-2 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-1 rounded-lg transition-colors"
              >
                {submitting ? 'Please wait…' : 'Accept Invite'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
